import { Router, Route, Switch } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { queryClient } from "@/services/queryClient";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CartProvider } from "@/contexts/CartContext";
import { UserProvider } from "@/contexts/UserContext";
import { GoogleAuthProvider } from "@/modules/google-auth";
import { FileManagerProvider } from "@/hooks/useFileManager";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import Layout from "@/components/layout";
import Home from "@/pages/home";
import EventsPage from "@/pages/events";
import EventDetailPage from "@/pages/event-detail";
import EventPhotosPage from "@/pages/event-photos";
import PhotoDetail from "@/pages/photo-detail";
import Cart from "@/pages/cart";
import Checkout from "@/pages/checkout";
import PaymentSuccessPage from "@/pages/payment-success";
import OrderConfirmation from "@/pages/order-confirmation";
import PhotoUpload from "@/pages/photo-upload";
import FormUploader from "@/pages/form-uploader";
import PhotoSelector from "@/pages/photo-selector";
import PhotoViewer from "@/pages/photo-viewer";
import PhotoUploadFlow from "@/pages/photo-upload-flow";
import EventGallery from "@/pages/event-gallery";
import LoginPage from "@/pages/login";
import RegisterPage from "@/pages/register";
import ProfilePage from "@/pages/profile";
import FavoritesPage from "@/pages/favorites";
import NotificationsPage from "@/pages/notifications";
import OrdersPage from "@/pages/orders";
import OrderStatusPage from "@/pages/order-status";
import AdminDashboard from "@/pages/admin-dashboard";
import PhotographerDashboard from "@/pages/photographer-dashboard";
import AthleteDashboard from "@/pages/athlete-dashboard";
import SystemStatus from "@/pages/system-status";
import UploaderDashboard from "@/uploader/pages/dashboard";
import PhotoProcessor from "@/uploader/pages/photo-processor";
import BatchPhotosPage from "@/pages/batch-photos";
import PhotoPreviewPage from "@/pages/photo-preview";
import EventsManagement from "@/pages/events-management";
import EventForm from "@/pages/event-form";
import AdvancedSearchPage from "@/pages/advanced-search";
import ApprovalsManagement from "@/approvals/pages/approvals-management";
import ContactPage from "@/pages/contact";
import TermsPage from "@/pages/terms";
import PrivacyPage from "@/pages/privacy";
import EmailManagementPage from "@/pages/email-management";
// import CartTest from "@/pages/cart-test";

import NotFound from "@/pages/not-found";
import DashboardRouter from "@/components/DashboardRouter";
import { isProduction, getApiBaseUrl, getStorageBaseUrl } from "@/config/production";
import { logEnvironmentVariables, testServiceConnectivity } from "@/utils/env-logger";


// Componente simples para health check
function HealthCheck() {
  const healthData = {
    status: 'healthy',
    service: 'frontend',
    timestamp: new Date().toISOString(),
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    uptime: Math.floor((Date.now() - (window as any).startTime || Date.now()) / 1000),
    environment: {
      mode: import.meta.env.MODE,
      prod: import.meta.env.PROD,
      isProduction: isProduction(),
      apiBaseUrl: getApiBaseUrl(),
      storageBaseUrl: getStorageBaseUrl()
    }
  };

  // Para requisições de API, retorna JSON puro
  if (window.location.pathname === '/health') {
    return (
      <div className="font-mono p-5">
        <pre>{JSON.stringify(healthData, null, 2)}</pre>
      </div>
    );
  }

  return (
    <div className="font-mono p-5">
      <pre>{JSON.stringify(healthData, null, 2)}</pre>
    </div>
  );
}

function AppRouter() {
  return (
    <Switch>
      {/* Rotas que não usam Layout (login e register têm seu próprio layout completo) */}
      <Route path="/login" component={LoginPage} />
      <Route path="/register" component={RegisterPage} />
      
      {/* Todas as outras rotas usam o Layout padrão */}
      <Route>
        <Layout>
          <Switch>
            <Route path="/health" component={HealthCheck} />
            <Route path="/" component={Home} />
            <Route path="/events" component={EventsPage} />
            <Route path="/event/:id" component={EventDetailPage} />
            <Route path="/events/:eventId/photos" component={EventPhotosPage} />
            <Route path="/photos/:photoId" component={PhotoDetail} />
            <Route path="/search" component={AdvancedSearchPage} />
            <Route path="/upload" component={PhotoUpload} />
            <Route path="/form-uploader" component={FormUploader} />
            <Route path="/photo-upload-flow" component={PhotoUploadFlow} />
            <Route path="/photo-selector" component={PhotoSelector} />
            <Route path="/photo-viewer" component={PhotoViewer} />
            <Route path="/event/:eventId/gallery" component={EventGallery} />
            <Route path="/profile" component={ProfilePage} />
            <Route path="/favorites" component={FavoritesPage} />
            <Route path="/notifications" component={NotificationsPage} />
            <Route path="/orders" component={OrdersPage} />
            <Route path="/order-status/:orderId" component={OrderStatusPage} />
            <Route path="/system-status" component={SystemStatus} />
            <Route path="/contact" component={ContactPage} />
            <Route path="/terms" component={TermsPage} />
            <Route path="/privacy" component={PrivacyPage} />

            {/* Rotas do Uploader Dashboard - Versão original */}
            <Route path="/uploader/dashboard" component={UploaderDashboard} />
            <Route path="/uploader/photo-processor">
              <PhotoProcessor files={[]} onBack={() => { }} />
            </Route>
            <Route path="/uploader/photos/:batchId" component={BatchPhotosPage} />
            <Route path="/photo/:photoId" component={PhotoPreviewPage} />

            {/* Rotas de Eventos */}
            <Route path="/events/management" component={EventsManagement} />
            <Route path="/events/create" component={EventForm} />
            <Route path="/events/:id/edit" component={EventForm} />
            <Route path="/approvals" component={ApprovalsManagement} />
            <Route path="/users/:userId/dashboard">
              {(params) => {
                if (!params?.userId) {
                  return <div>ID de usuário inválido</div>;
                }
                return <DashboardRouter userId={params.userId} />;
              }}
            </Route>
            <Route path="/cart" component={Cart} />
            {/* <Route path="/cart-test" component={CartTest} /> */}
            <Route path="/checkout" component={Checkout} />
            <Route path="/payment/success" component={PaymentSuccessPage} />
            <Route path="/admin-dashboard" component={AdminDashboard} />
            <Route path="/admin/emails" component={EmailManagementPage} />
            <Route path="/photographer-dashboard" component={PhotographerDashboard} />
            <Route path="/athlete-dashboard" component={AthleteDashboard} />
            <Route path="/photo-upload" component={PhotoUpload} />
            <Route path="/orders/:orderId" component={OrderConfirmation} />
            <Route component={NotFound} />
          </Switch>
        </Layout>
      </Route>
    </Switch>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <UserProvider>
          <GoogleAuthProvider>
            <FileManagerProvider>
              <CartProvider>
                <FavoritesProvider>
                  <NotificationProvider>
                    <Router>
                      <AppRouter />
                    </Router>
                    <Toaster />
                  </NotificationProvider>
                </FavoritesProvider>
              </CartProvider>
            </FileManagerProvider>
          </GoogleAuthProvider>
        </UserProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
